import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { companyInfo, images } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock, Navigation, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero - Compact */}
      <section className="pt-6 pb-10 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={images.building} alt="Contact" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/95 via-[#0a1628]/80 to-[#0a1628]/60" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Contact <span className="text-[#87CEEB]">Us</span></h1>
          <p className="text-base text-white/80">We're here to answer your questions about clinical trials.</p>
        </div>
      </section>

      {/* Quick Contact Bar */}
      <section className="bg-[#1877F2] py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-4 text-white">
            <a href={`tel:${companyInfo.contact.phone}`} className="flex items-center space-x-2 hover:text-[#87CEEB] transition-colors" data-testid="contact-quick-phone">
              <Phone size={18} />
              <span className="font-semibold">{companyInfo.contact.phone}</span>
            </a>
            <span className="hidden md:inline text-white/50">|</span>
            <a href={`mailto:${companyInfo.contact.email}`} className="flex items-center space-x-2 hover:text-[#87CEEB] transition-colors" data-testid="contact-quick-email">
              <Mail size={18} />
              <span className="font-semibold">{companyInfo.contact.email}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Content - Compact */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Form */}
            <Card className="border-0 shadow-xl rounded-xl">
              <CardContent className="p-5">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Send a Message</h2>
                <p className="text-sm text-gray-600 mb-4">Your message will be sent to <span className="font-semibold text-[#1877F2]">{companyInfo.contact.email}</span></p>
                {submitted ? (
                  <div className="flex flex-col items-center py-6 text-center" data-testid="contact-success-message">
                    <CheckCircle className="w-12 h-12 text-green-500 mb-2" />
                    <h3 className="text-lg font-semibold">Message Sent!</h3>
                    <p className="text-gray-600 text-sm">We'll get back to you soon.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
                    {error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
                        {error}
                      </div>
                    )}
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input placeholder="Your name *" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="h-10 rounded-lg" data-testid="contact-name-input" disabled={loading} />
                      <Input type="email" placeholder="Email *" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="h-10 rounded-lg" data-testid="contact-email-input" disabled={loading} />
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="h-10 rounded-lg" data-testid="contact-phone-input" disabled={loading} />
                      <Input placeholder="Subject *" required value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="h-10 rounded-lg" data-testid="contact-subject-input" disabled={loading} />
                    </div>
                    <Textarea placeholder="Your message..." required rows={3} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="rounded-lg" data-testid="contact-message-input" disabled={loading} />
                    <Button type="submit" className="w-full bg-[#1877F2] hover:bg-[#166FE5] text-white py-4 rounded-lg font-semibold" data-testid="contact-submit-btn" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" /> Send Message
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-4">
              <h2 className="text-xl font-bold text-gray-900 mb-3">Get in Touch</h2>
              
              {/* Contact Cards */}
              <div className="grid grid-cols-2 gap-3">
                <a href={`tel:${companyInfo.contact.phone}`} className="block" data-testid="contact-phone-card">
                  <Card className="border-0 shadow-md rounded-xl hover:shadow-lg transition-all h-full">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1877F2] to-[#87CEEB] rounded-lg flex items-center justify-center mx-auto mb-2">
                        <Phone className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Call Us</h3>
                      <p className="text-[#1877F2] font-medium text-sm">{companyInfo.contact.phone}</p>
                    </CardContent>
                  </Card>
                </a>
                <a href={`mailto:${companyInfo.contact.email}`} className="block" data-testid="contact-email-card">
                  <Card className="border-0 shadow-md rounded-xl hover:shadow-lg transition-all h-full">
                    <CardContent className="p-4 text-center">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1877F2] to-[#87CEEB] rounded-lg flex items-center justify-center mx-auto mb-2">
                        <Mail className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-900 text-sm">Email Us</h3>
                      <p className="text-[#1877F2] font-medium text-xs break-all">{companyInfo.contact.email}</p>
                    </CardContent>
                  </Card>
                </a>
              </div>

              {/* Address Card */}
              <Card className="border-0 shadow-md rounded-xl">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1877F2] to-[#87CEEB] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-sm mb-1">Visit Us</h3>
                      <p className="text-gray-600 text-sm mb-2">{companyInfo.contact.address}</p>
                      <a 
                        href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.contact.address)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-1 text-[#1877F2] font-semibold text-sm hover:underline"
                        data-testid="contact-directions-btn"
                      >
                        <Navigation size={14} />
                        <span>Get Directions</span>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hours Card */}
              <Card className="border-0 shadow-md rounded-xl bg-[#F8FBFF]">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="w-5 h-5 text-[#1877F2]" />
                    <h3 className="font-bold text-gray-900 text-sm">Business Hours</h3>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monday - Friday</span>
                      <span className="font-medium text-gray-900">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Saturday - Sunday</span>
                      <span className="text-gray-500">Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
